import {
    createContext,
    destroyContext,
    domToCanvas
} from "modern-screenshot";
import {
    drawGuillotine,
    tokenStateAt as executionStateAt
} from "../utils/execution-scene.js";
import {
    drawDemonKill,
    tokenStateAt as demonKillStateAt
} from "../utils/demon-kill-scene.js";

import { drawCondition, conditionTokenStateAt } from "../utils/condition-scene.js";
import {
    isWebKit,
    patchCloneForWebKit
} from "../utils/webkit-screenshot.js";

export default class GrimoireTimeline {

    constructor(element, frameStore = null) {
        this.element = element;
        this.frames = [];
        this.fastFrameIndexes = new Set();
        this.frameStore = frameStore;
    }

    /**
     * Loads previously persisted frames from the frame store.
     */
    async restore() {

        if (!this.frameStore) {
            return;
        }

        try {

            const records = (await this.frameStore.getAll()).map((record) => (
                record?.blob !== undefined
                ? record
                : { blob: record, fast: false }
            ));
            this.frames = records.map((record) => record.blob);
            this.fastFrameIndexes = new Set(
                records
                    .map((record, index) => (record.fast ? index : -1))
                    .filter((index) => index >= 0)
            );

        } catch (error) {
            console.warn("Unable to restore grimoire frames", error);
        }

    }

    /**
     * Renders the given element to a canvas, bounded by a 5 second timeout.
     *
     * @param  {Element} element
     *         Element to render.
     * @param  {Number} scale
     *         The scale to render at.
     * @return {Promise.<HTMLCanvasElement>}
     */
    async renderElement(element, scale) {

        let context;
        let expired = false;
        let timeout;

        const render = async () => {
            context = await createContext(element, {
                backgroundColor: "#ffffff",
                filter: (node) => !node.classList?.contains("pad-wrapper__icon"),
                scale,
                timeout: 4000,
                onCloneNode: (clone) => {
                    if (isWebKit()) {
                        patchCloneForWebKit(clone);
                    }
                },
                onCreateForeignObjectSvg: () => {
                    // WebKit needs decode retries, but the library schedules one
                    // per image/background, including repeated token textures.
                    // Bound that work independently of the number of players.
                    context.drawImageCount = Math.min(context.drawImageCount, 2);
                }
            });

            try {
                if (!expired) {
                    // Native SVG/foreignObject rendering preserves CSS filters,
                    // shadows, pseudo-elements and SVG icons without restyling tokens.
                    return await domToCanvas(context);
                }
            } finally {
                if (context) {
                    destroyContext(context);
                    context = null;
                }
            }
        };

        try {
            return await Promise.race([
                render(),
                new Promise((resolve, reject) => {
                    timeout = setTimeout(() => reject(new Error("Grimoire capture timed out")), 5000);
                })
            ]);
        } finally {
            expired = true;
            clearTimeout(timeout);
            if (context) {
                destroyContext(context);
                context = null;
            }
        }

    }

    /**
     * Encodes the given canvas as a JPEG blob.
     *
     * @param  {HTMLCanvasElement} canvas
     * @return {Promise.<Blob>}
     */
    canvasToBlob(canvas, quality = 0.88) {

        return new Promise((resolve, reject) => {
            canvas.toBlob((result) => {
                if (result) {
                    resolve(result);
                } else {
                    reject(new Error("Unable to encode grimoire frame"));
                }
            }, "image/jpeg", quality);
        });

    }

    /**
     * Stores the frame blob, provided the frame list has not been cleared
     * while it was rendering.
     *
     * @param {Blob} blob
     * @param {Array.<Blob>} frames
     *        The frame list captured before the render started; storing is
     *        skipped if it no longer matches the current one.
     * @param {Boolean} fast
     *        Whether the frame should be played back faster in the video.
     */
    async saveFrame(blob, frames, fast) {

        if (frames !== this.frames) {
            return;
        }

        if (fast) {
            this.fastFrameIndexes.add(frames.length);
        }

        frames.push(blob);

        if (this.frameStore) {

            // Persistence is best-effort: a failing store must not abort a
            // capture sequence or lose the in-memory frame.
            try {
                await this.frameStore.add(blob, fast);
            } catch (error) {
                console.warn("Unable to persist grimoire frame", error);
            }

        }

    }

    async capture(label, { fast = false } = {}) {

        if (!this.element || !label) {
            return;
        }

        const frames = this.frames;

        try {
            const canvas = await this.renderElement(
                this.element,
                fast ? 0.75 : 1.5
            );
            const blob = await this.canvasToBlob(canvas);
            await this.saveFrame(blob, frames, fast);
        } catch (error) {
            // A missing animation frame must not prevent a phase change.
            console.warn("Unable to capture grimoire frame", error);
        }
    }

    /**
     * Pre-renders `steps` frames of the token's execution animation (the
     * guillotine scene). See `captureTokenAnimation()`.
     *
     * @param {Element} element
     *        The `.character` element being animated.
     * @param {Object} [options]
     * @param {Number} [options.steps=18]
     *        Number of frames to capture.
     */
    captureExecution(element, { steps = 18 } = {}) {
        return this.captureTokenAnimation(element, {
            className: "is-executed",
            label: "Execution",
            tokenStateAt: executionStateAt,
            drawScene: drawGuillotine,
            steps
        });
    }

    /**
     * Pre-renders `steps` frames of the token's demon kill animation (the
     * shadow and claws scene). See `captureTokenAnimation()`.
     *
     * @param {Element} element
     *        The `.character` element being animated.
     * @param {Object} [options]
     * @param {Number} [options.steps=18]
     *        Number of frames to capture.
     */
    captureDemonKill(element, { steps = 18 } = {}) {
        return this.captureTokenAnimation(element, {
            className: "is-demon-killed",
            label: "Demon kill",
            tokenStateAt: demonKillStateAt,
            drawScene: drawDemonKill,
            steps
        });
    }

    /**
     * Pre-renders `steps` frames of a token death animation: the token is
     * drawn with the scene's keyframes applied and the scene is drawn
     * procedurally on top, both over a single base capture of the pad. The
     * animation class is removed for the two DOM renders so both images show
     * the clean "dead" state, then re-added so the live CSS animation replays
     * for the user. All frames are marked for faster playback in the video.
     *
     * @param {Element} element
     *        The `.character` element being animated.
     * @param {Object} options
     * @param {String} options.className
     *        Class that runs the live CSS animation.
     * @param {String} options.label
     *        Name used in the capture log.
     * @param {Function} options.tokenStateAt
     *        Returns the token transform/filter at normalised time `t`.
     * @param {Function} options.drawScene
     *        Draws the scene `(context, x, y, size, t)`.
     * @param {Number} options.steps
     *        Number of frames to capture.
     */
    async captureTokenAnimation(element, {
        className,
        label,
        tokenStateAt,
        drawScene,
        steps
    }) {

        if (!this.element) {
            return;
        }

        const wrapper = element.closest(".js--token--wrapper") || element;
        const frames = this.frames;
        const animationVersion = element.animationVersion;
        const scale = 1.5;

        try {
            element.classList.remove(className);
            const baseCanvas = await this.renderElement(this.element, scale);
            const tokenCanvas = await this.renderElement(wrapper, scale);
            if (element.animationVersion !== animationVersion || frames !== this.frames) {
                return;
            }
            element.classList.add(className);

            const host = this.element.getBoundingClientRect();
            const bounds = wrapper.getBoundingClientRect();
            const left = (bounds.left - host.left) * scale;
            const top = (bounds.top - host.top) * scale;
            const width = bounds.width * scale;
            const height = bounds.height * scale;

            const frameCanvas = document.createElement("canvas");
            frameCanvas.width = baseCanvas.width;
            frameCanvas.height = baseCanvas.height;
            const context = frameCanvas.getContext("2d");

            let captured = 0;

            for (let i = 0; i < steps; i += 1) {

                const t = i / (steps - 1);
                const state = tokenStateAt(t);

                context.drawImage(baseCanvas, 0, 0);
                context.save();
                context.filter = state.filter;
                context.translate(
                    left + width / 2 + state.x * width,
                    top + height / 2 + state.y * height
                );
                context.rotate(state.rotate * Math.PI / 180);
                context.drawImage(tokenCanvas, -width / 2, -height / 2, width, height);
                context.restore();

                drawScene(context, left, top, width, t);

                const blob = await this.canvasToBlob(frameCanvas, 0.75);
                await this.saveFrame(blob, frames, true);
                captured += 1;

            }

            console.info(`${label} capture: ${captured}/${steps} frames`);

        } catch (error) {
            if (element.animationVersion === animationVersion && frames === this.frames) {
                element.classList.add(className);
            }
            console.warn("Unable to capture grimoire frame", error);
        }

    }

    captureCondition(element, kind, { steps = 32 } = {}) {
        return this.captureTokenAnimation(element, {
            className: kind === "poisoned" ? "is-being-poisoned" : "is-becoming-drunk",
            label: kind,
            tokenStateAt: conditionTokenStateAt,
            drawScene: (context, x, y, size, t) => drawCondition(context, x, y, size, t, kind),
            steps
        });
    }

    createFrames() {
        return Promise.resolve(this.frames.slice());
    }

    /**
     * Returns the indexes of the frames that should be played back faster than
     * the standard frame duration (e.g. execution animation frames).
     *
     * @return {Set.<Number>}
     */
    getFastFrameIndexes() {
        return new Set(this.fastFrameIndexes);
    }

    clear() {

        this.frames = [];
        this.fastFrameIndexes = new Set();

        if (this.frameStore) {
            return this.frameStore.clear().catch((error) => {
                console.warn("Unable to clear persisted grimoire frames", error);
            });
        }

    }

}
